import React, { useState, useReducer, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";

import { SchedulerProvider } from "./schedulerContext";
import useInteraction from "../UserSurveys/useInteractions";
import getEvent, { initInteraction } from "./eventModel";
import eventsReducer from "./eventsReducer";
import Calendar from "./Calendar/index";
import Alias from "../User/Alias";
import AddButton from "./AddButton";
import BackButton from "../Navbar/BackButton";
import useScheduler from "./useScheduler";
import AppContext from "../appContext";
import EditorMessage from "../Components/EditorMessage";
import useSaveMutation from "./useSaveMutation";
import MutationMessage from "../Components/MutationMessage";
import Legend from "./Legend";

const toolbarHeight = 8;

export default function Scheduler(props) {
  const { t } = useTranslation();
  const {
    app: {
      client: { editable, writeAccess },
    },
  } = useContext(AppContext);
  const disabled = !editable || !writeAccess;
  // local state
  const [events, eventsDispatch] = useReducer(eventsReducer, []);
  const [initEvents, setInitEvents] = useState([]);
  const [interaction, setInteraction] = useState({
    ...initInteraction,
    open: false,
  });
  const [lastSaved, setLastSaved] = useState("");

  // server state
  const { interactions } = useInteraction();
  const { scheduler, isSchedulerLoading, refetchScheduler } = useScheduler();
  const saveMutation = useSaveMutation({
    events,
    onSuccess: (data) => {
      refetchScheduler();
      setInitEvents(events);
      /*       if (data?.lastSaved) {
        setLastSaved(data.lastSaved);
      } */
    },
  });

  // constructor
  useEffect(() => {
    const getLocalInteractionById = (id) =>
      interactions.find((interaction) => interaction.id === id);

    const getInteraction = (event) => {
      if (event.itemId === null && event.templateId > 0 && event.templateName)
        return {
          id: null,
          name: event.templateName,
        };
      if (event.originalName !== null)
        return { id: event.itemId, name: event.originalName };
      return getLocalInteractionById(event.itemId);
    };
    if (scheduler?.lastSaved) {
      setLastSaved(scheduler.lastSaved);
    }

    if (scheduler?.data) {
      // init calendar events
      const s = scheduler.data.map((event) =>
        getEvent({
          type: "SERVER_EVENT",
          payload: {
            event,
            interaction: getInteraction(event) ?? {
              name: initInteraction.name,
              id: event.itemId,
            },
          },
        })
      );
      eventsDispatch({ type: "SET_EVENTS", payload: s });
      setInitEvents(s);

      // init addButton
      const selectedInteraction = getLocalInteractionById(
        props?.match?.params?.itemId
      );
      if (selectedInteraction) {
        setInteraction({ ...selectedInteraction, open: false });
      } else if (interactions.length > 0) {
        setInteraction({
          ...interactions[interactions.length - 1],
          open: false,
        }); // load the first survey by default
      }
    }
  }, [scheduler, interactions]);

  useEffect(() => {
    if (["RELOAD"].includes(props.location.state?.type)) {
      refetchScheduler();
    }
  }, [props]);

  if (isSchedulerLoading) return <LinearProgress />;
  return (
    <React.Fragment>
      <MutationMessage
        mutation={saveMutation}
        text={{
          loading: t("common.notifications.saveLoading"),
          success: t("common.notifications.saveSuccess"),
        }}
      />
      <EditorMessage lastSaved={lastSaved} />
      <SchedulerProvider
        value={{
          events,
          eventsDispatch,
          interaction,
          setInteraction,
          initEvents,
          saveMutation,
        }}
      >
        <Box display="flex">
          <Box width={250}>
            <Box display="flex" height={`${toolbarHeight}vh`}>
              <BackButton />
              <Alias size="small" />
            </Box>
            <Box borderTop="1px solid #ddd" padding={2} width={250}>
              <AddButton disabled={disabled} />
            </Box>
            <Box padding={2} position={"fixed"} bottom={1}>
            <Legend />
            </Box>
          </Box>
          <Calendar locked={disabled} />
        </Box>
      </SchedulerProvider>
    </React.Fragment>
  );
}
